<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  parameter: { required: true },
  item: { type: Object, required: true },
  type: { require: false, default: 'number' },
  show: { required: false, default: true },
  editable: { require: false, default: true },
  dirtyClass: { required: false },
  width: { default: '100%', required: false },
  fontSize: { default: '14px', required: false },
  disabled: { default: false, required: false },
  commitOnBlur: { type: Boolean, default: false },
  validator: { type: Function, default: () => true }
})

const emit = defineEmits(['update', 'set', 'changeValue'])

function getNestedValue() {
  return props.parameter.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), props.item)
}

function commitValue(raw) {
  emit('update')
  emit('set', { item: props.item, value: raw === '' ? null : raw, parameter: props.parameter })
}

const originalValue = ref(null)
const localValue = ref(getNestedValue())

// Single v-model target — delegates based on commitOnBlur prop
const fieldValue = computed({
  get() {
    return props.commitOnBlur ? localValue.value : getNestedValue()
  },
  set(newValue) {
    if (props.commitOnBlur) {
      localValue.value = newValue
    } else {
      commitValue(newValue)
    }
  }
})

function onFocus() {
  originalValue.value = localValue.value
}

function onBlur() {
  const raw = localValue.value === '' ? null : localValue.value
  if (props.validator(raw)) {
    commitValue(raw)
  } else {
    localValue.value = originalValue.value
  }
}

function onKeydown(event) {
  if (event.key === 'Enter') {
    event.target.blur()
  }
}
</script>

<template>
  <div :class="`fill-height ${dirtyClass}`" style="display: flex; align-items: center">
    <v-text-field
      color="primary"
      full-width
      class="pl-1 number-input-type"
      :style="`font-weight: normal; border-radius: 0; min-width: ${width};`"
      hide-details
      solo
      variant="plain"
      density="compact"
      :readonly="!editable"
      :disabled="disabled"
      :type="type"
      v-model="fieldValue"
      v-bind="commitOnBlur ? { onFocus, onBlur, onKeydown } : {}"
    ></v-text-field>
  </div>
</template>

<style scoped>
.number-input-type input[type='number'] {
  -moz-appearance: textfield;
}

:deep(.v-field__input) {
  padding: 0 !important;
}

:deep(input[type='number']) {
  font-size: v-bind('fontSize') !important;
}
</style>
